import { registerMockAPI } from '~/mocks/mockApis';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { axiosInstance } from '~/services';

import { API_ENDPOINT } from '~/constants';

import { deliveryAgencyKeys } from './keys';

import type { TypeGetOutboundsListParams, TypeGetOutboundsListResponse } from '~/types/delivery-agency.types';

/**
 * [합포 단위 관리] 출고 단위 리스트 조회 API
 */
export const fetchOutboundsList = async (params: TypeGetOutboundsListParams): Promise<TypeGetOutboundsListResponse> => {
  const { data } = await axiosInstance.get<TypeGetOutboundsListResponse>(
    API_ENDPOINT.DELIVERY_AGENCY.GET_OUTBOUNDS_LIST,
    { params }
  );
  return data;
};

type UseGetOutboundsListQueryOptions = {
  params: TypeGetOutboundsListParams;
  enabled?: boolean;
  showGlobalErrorToast?: boolean;
};

/**
 * [합포 단위 관리] 출고 단위 리스트 조회 Query Hook
 */
export const useGetOutboundsListQuery = ({
  params,
  enabled = true,
  showGlobalErrorToast = true,
}: UseGetOutboundsListQueryOptions) => {
  return useQuery({
    queryKey: deliveryAgencyKeys.outboundsList(params),
    queryFn: () => fetchOutboundsList(params),
    enabled,
    staleTime: 0,
    gcTime: 1000 * 30,
    placeholderData: keepPreviousData,
    meta: { showGlobalErrorToast },
  });
};

/**
 * [합포 단위 관리] 출고 단위 리스트 조회 Mock
 *
 * @remarks
 * DevTools에서 Mock을 활성화하면 응답합니다. 배송예정일은 기본 검색 조건(내일, 2026-05-20)과 맞춘다.
 */
registerMockAPI<TypeGetOutboundsListResponse>({
  method: 'get',
  endpoint: '/api-delivery-agency/outbounds/list',
  label: '합포 단위 관리/출고 단위 리스트',
  scenarios: [
    {
      name: '정상 응답',
      response: {
        data: [
          {
            estimatedDeliveryDate: '2026-05-20',
            outboundCode: 'OB20260519001',
            isOutboundUnitConfigurable: true,
            orderCount: 1,
            clientOrderCodes: ['ORD-20260519-0001'],
            shippingLabels: ['210-C0-OB20260519001-0001'],
            ordererName: '홍길동',
            receiverName: '홍길동',
            deliveryType: { code: 'DAWN', description: '샛별배송' },
            courierCodeCode: { code: 'NM', description: '넥스트마일' },
          },
          {
            estimatedDeliveryDate: '2026-05-20',
            outboundCode: 'OB20260519002',
            isOutboundUnitConfigurable: true,
            orderCount: 3,
            clientOrderCodes: ['ORD-20260519-0002', 'ORD-20260519-0003', 'ORD-20260519-0004'],
            shippingLabels: ['210-C0-OB20260519002-0001', '210-C0-OB20260519002-0002'],
            ordererName: '김주문',
            receiverName: '김수령',
            deliveryType: { code: 'DAWN', description: '샛별배송' },
            courierCodeCode: { code: 'NM', description: '넥스트마일' },
          },
          {
            estimatedDeliveryDate: '2026-05-20',
            outboundCode: 'OB20260519005',
            isOutboundUnitConfigurable: false,
            orderCount: 1,
            clientOrderCodes: ['ORD-20260519-0005'],
            shippingLabels: ['210-C0-OB20260519005-0001'],
            ordererName: '이주문',
            receiverName: '이배송',
            deliveryType: { code: 'DAY', description: '하루배송' },
            courierCodeCode: { code: 'CJT', description: 'CJ' },
          },
          {
            estimatedDeliveryDate: '2026-05-20',
            outboundCode: 'OB20260519006',
            isOutboundUnitConfigurable: true,
            orderCount: 2,
            clientOrderCodes: ['ORD-20260519-0006', 'ORD-20260519-0007'],
            shippingLabels: [],
            ordererName: '박주문',
            receiverName: '박완료',
            deliveryType: { code: 'DAWN', description: '샛별배송' },
            courierCodeCode: { code: 'LTT', description: '롯데' },
          },
        ],
        pagination: { cursor: 1, listView: 50, listTotal: 4 },
      },
    },
    {
      name: '데이터 없음',
      response: {
        data: [],
        pagination: { cursor: 1, listView: 50, listTotal: 0 },
      },
    },
  ],
});
